import React, { useState } from "react";
import axios from "axios";
import { Mail, ArrowRight, CheckCircle2, AlertTriangle, Phone } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const INITIAL = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  business_name: "",
  business_type: "",
  monthly_volume: "",
  website: "",
  current_processor: "",
  message: "",
};

export default function ContactPage() {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const onChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, form);
      setStatus({
        state: "success",
        msg: "Submission received. A MerchantCore specialist will reach out within 1 business day.",
      });
      setForm(INITIAL);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Something went wrong. Please try again.";
      setStatus({ state: "error", msg: typeof detail === "string" ? detail : "Please check your inputs and try again." });
    }
  };

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="contact-hero">
        <div aria-hidden className="pointer-events-none absolute inset-0 grid-overlay opacity-60" />
        <div aria-hidden className="pointer-events-none absolute -top-40 right-10 h-[420px] w-[420px] rounded-full bg-emerald/10 blur-[120px]" />
        <div className="container-wide relative z-10 grid items-end gap-10 py-20 lg:grid-cols-12 lg:py-24">
          <div className="lg:col-span-9">
            <span className="eyebrow">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald" /> Contact MerchantCore
            </span>
            <h1 className="mt-7 font-display text-4xl font-bold leading-[1.02] tracking-tight text-cloud sm:text-6xl lg:text-7xl">
              Claim your <span className="text-gradient">free</span>
              <br />
              consultation today.
            </h1>
          </div>
          <div className="lg:col-span-3">
            <p className="font-sans text-sm leading-relaxed text-muted">
              Ready to transform your payment processing? Submit the form and a
              specialist will deliver a custom, no-obligation quote.
            </p>
          </div>
        </div>
      </section>

      {/* FORM + INFO */}
      <section className="pb-24" data-testid="contact-form-section">
        <div className="container-wide grid gap-6 lg:grid-cols-12">
          {/* LEFT: contact info */}
          <aside className="card lg:col-span-4">
            <span className="eyebrow">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald" /> Direct channels
            </span>
            <h2 className="mt-5 font-display text-2xl font-semibold tracking-tight text-cloud">
              We're here to help.
            </h2>
            <p className="mt-4 font-sans text-sm leading-relaxed text-muted">
              Questions about merchant accounts, compliance, or alternative
              payment methods — reach out directly or use the form.
            </p>

            <ul className="mt-8 space-y-5 border-t border-line pt-6">
              <ContactRow icon={Mail} label="Email" value="hello@merchantcore.us" href="mailto:hello@merchantcore.us" testid="contact-email" />
              <ContactRow icon={Phone} label="Support" value="Dedicated merchant assistance" testid="contact-phone" />
            </ul>

            <div className="mt-8 rounded-2xl border border-line bg-white/[0.02] p-5">
              <h3 className="font-display text-base font-semibold tracking-tight text-cloud">What happens next?</h3>
              <ol className="mt-4 space-y-3.5">
                {[
                  "Review of your submission within 1 business day.",
                  "Discovery call with a payments specialist.",
                  "Custom quote and onboarding kickoff.",
                ].map((s, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald/15 font-mono text-[11px] font-semibold text-emerald">
                      {i + 1}
                    </span>
                    <span className="font-sans text-sm leading-relaxed text-cloud/85">{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          </aside>

          {/* RIGHT: form */}
          <form onSubmit={onSubmit} className="card lg:col-span-8" data-testid="contact-form">
            <div className="flex flex-wrap items-end justify-between gap-4 border-b border-line pb-5">
              <div>
                <span className="eyebrow">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald" /> Request a free quote
                </span>
                <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight text-cloud">
                  Tell us about your business
                </h2>
              </div>
              <span className="font-mono text-[11px] uppercase tracking-ultra text-faint">Form // 2026.06</span>
            </div>

            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              <Field label="First name" required>
                <input name="first_name" required value={form.first_name} onChange={onChange} className="input" placeholder="Jane" data-testid="contact-first-name" />
              </Field>
              <Field label="Last name" required>
                <input name="last_name" required value={form.last_name} onChange={onChange} className="input" placeholder="Doe" data-testid="contact-last-name" />
              </Field>
              <Field label="Email" required>
                <input type="email" name="email" required value={form.email} onChange={onChange} className="input" placeholder="jane@company.com" data-testid="contact-email-input" />
              </Field>
              <Field label="Phone" required>
                <input name="phone" required value={form.phone} onChange={onChange} className="input" placeholder="+1 ..." data-testid="contact-phone-input" />
              </Field>
              <Field label="Business name" required>
                <input name="business_name" required value={form.business_name} onChange={onChange} className="input" placeholder="Acme Inc." data-testid="contact-business-name" />
              </Field>
              <Field label="Business type" required>
                <select name="business_type" required value={form.business_type} onChange={onChange} className="input" data-testid="contact-business-type">
                  <option value="">Select type…</option>
                  <option>Retail</option>
                  <option>E-Commerce</option>
                  <option>B2B</option>
                  <option>Startup / SaaS</option>
                  <option>TeleMed</option>
                  <option>Peptides</option>
                  <option>Coach / Consultant</option>
                  <option>Creator / Publisher</option>
                  <option>Auto Dealership</option>
                  <option>High-Risk (CBD, Peptides, etc.)</option>
                  <option>Other</option>
                </select>
              </Field>
              <Field label="Monthly volume">
                <select name="monthly_volume" value={form.monthly_volume} onChange={onChange} className="input" data-testid="contact-monthly-volume">
                  <option value="">Select range…</option>
                  <option>Under $10,000</option>
                  <option>$10,000 – $25,000</option>
                  <option>$25,000 – $50,000</option>
                  <option>$50,000 – $100,000</option>
                  <option>$100,000 – $250,000</option>
                  <option>$250,000+</option>
                </select>
              </Field>
              <Field label="Website">
                <input name="website" value={form.website} onChange={onChange} className="input" placeholder="https://" data-testid="contact-website" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Current processor">
                  <input name="current_processor" value={form.current_processor} onChange={onChange} className="input" placeholder="e.g. Stripe, Square, or N/A" data-testid="contact-current-processor" />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Additional information">
                  <textarea name="message" value={form.message} onChange={onChange} rows={5} className="input resize-y" placeholder="Tell us about your business, pain points, and goals…" data-testid="contact-message" />
                </Field>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-line pt-6">
              <p className="max-w-md font-mono text-[11px] uppercase tracking-wide2 text-faint">
                By submitting, you agree to be contacted about MerchantCore services. We never share your information.
              </p>
              <button type="submit" disabled={status.state === "loading"} className="btn-primary" data-testid="contact-submit">
                {status.state === "loading" ? "Sending…" : "Submit request"}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>

            {status.state === "success" && (
              <div className="mt-6 flex items-start gap-3 rounded-xl border border-emerald/40 bg-emerald/10 p-4" data-testid="contact-success">
                <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald" />
                <span className="font-sans text-sm text-cloud">{status.msg}</span>
              </div>
            )}
            {status.state === "error" && (
              <div className="mt-6 flex items-start gap-3 rounded-xl border border-red-500/40 bg-red-500/10 p-4" data-testid="contact-error">
                <AlertTriangle className="mt-0.5 h-5 w-5 text-red-400" />
                <span className="font-sans text-sm text-red-300">{status.msg}</span>
              </div>
            )}
          </form>
        </div>
      </section>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="label">
        {label}
        {required && <span className="text-emerald"> *</span>}
      </span>
      {children}
    </label>
  );
}

function ContactRow({ icon: Icon, label, value, href, testid }) {
  const inner = (
    <div className="flex items-start gap-3.5">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-line bg-emerald/10 text-emerald">
        <Icon className="h-4 w-4" />
      </span>
      <div>
        <div className="font-mono text-[11px] uppercase tracking-ultra text-faint">{label}</div>
        <div className="mt-0.5 font-sans text-sm text-cloud">{value}</div>
      </div>
    </div>
  );
  return (
    <li data-testid={testid}>
      {href ? <a href={href} className="block transition-colors hover:text-emerald">{inner}</a> : inner}
    </li>
  );
}
